import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormikContext } from 'formik';
import { parseISO } from 'date-fns';
import type { ReadonlyDeep } from 'type-fest';

import { DatePicker } from '@mui/x-date-pickers';
import Stack from '@mui/material/Stack';

import { EMPTY_STRING } from '@e-socna/empty';
import { getFragmentData, graphql } from '@e-socna/data-layer';
import type { FolderArchiveInfoInput } from '@e-socna/data-layer';

import { useFolderReadyContext } from '../../contexts/FolderContext/hooks/useFolderReadyContext.hook';

import { useInitialValuesFactory } from './hooks/useInitialValuesFactory.hook';
import { folderArchiveInfoForm2ApiMapper } from './mappers/folder-archive-info.form2api.mapper';
import { isFolderArchiveInfoComplete } from './utils/folder-archive-info-complete.util';

interface FolderArchiveInfoFormValues {
  readonly actionPayload?: Readonly<{
    archiveInfo: ReadonlyDeep<FolderArchiveInfoInput>;
  }> | null;
}

export const FolderArchiveInfoDialog_Folder_Fragment = graphql(`
  fragment FolderArchiveInfoDialog_Folder_Fragment on Folder {
    missions {
      _id
      archiveInfo {
        startDateForAnalytics
        endDateForAnalytics
      }
    }
  }
`);

export const FolderArchiveInfoFields = () => {
  const { folder: rawFolder } = useFolderReadyContext();
  const { missions } = getFragmentData(
    FolderArchiveInfoDialog_Folder_Fragment,
    rawFolder,
  );

  const {
    mostRecentMissionEndDateForAnalytics,
    oldestMissionStartDateForAnalytics,
  } = useMemo(() => {
    const startDates = missions
      .map((mission) => mission.archiveInfo?.startDateForAnalytics)
      .filter((date): date is string => !!date);

    const endDates = missions
      .map((mission) => mission.archiveInfo?.endDateForAnalytics)
      .filter((date): date is string => !!date);

    return {
      oldestMissionStartDateForAnalytics: startDates.length
        ? startDates.reduce((oldest, date) => (date < oldest ? date : oldest))
        : undefined,
      mostRecentMissionEndDateForAnalytics: endDates.length
        ? endDates.reduce((mostRecent, date) =>
            date > mostRecent ? date : mostRecent,
          )
        : undefined,
    };
  }, [missions]);

  const { setFieldValue } = useFormikContext<FolderArchiveInfoFormValues>();

  const { initialValues } = useInitialValuesFactory({
    startDateForAnalytics: oldestMissionStartDateForAnalytics,
    endDateForAnalytics: mostRecentMissionEndDateForAnalytics,
  });

  const [draft, setDraft] = useState(initialValues);

  const isComplete = isFolderArchiveInfoComplete(draft);

  useEffect(() => {
    void setFieldValue(
      'actionPayload',
      isComplete
        ? { archiveInfo: folderArchiveInfoForm2ApiMapper(draft) }
        : null,
    );
  }, [draft, isComplete, setFieldValue]);

  const startDateForAnalyticsValue = useMemo(
    () =>
      draft.startDateForAnalytics
        ? parseISO(draft.startDateForAnalytics)
        : null,
    [draft.startDateForAnalytics],
  );

  const endDateForAnalyticsValue = useMemo(
    () =>
      draft.endDateForAnalytics ? parseISO(draft.endDateForAnalytics) : null,
    [draft.endDateForAnalytics],
  );

  const handleStartDateChange = useCallback((date: Date | null) => {
    setDraft((previous) => ({
      ...previous,
      startDateForAnalytics: date ? date.toISOString() : EMPTY_STRING,
    }));
  }, []);

  const handleEndDateChange = useCallback((date: Date | null) => {
    setDraft((previous) => ({
      ...previous,
      endDateForAnalytics: date ? date.toISOString() : EMPTY_STRING,
    }));
  }, []);

  return (
    <Stack spacing={2}>
      <DatePicker
        label="Date de début pour analytics"
        maxDate={endDateForAnalyticsValue ?? undefined}
        onChange={handleStartDateChange}
        slotProps={{ textField: { required: true } }}
        value={startDateForAnalyticsValue}
      />
      <DatePicker
        label="Date de fin pour analytics"
        minDate={startDateForAnalyticsValue ?? undefined}
        onChange={handleEndDateChange}
        slotProps={{ textField: { required: true } }}
        value={endDateForAnalyticsValue}
      />
    </Stack>
  );
};
